import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal } from './ui/Modal';
import VaultPassphraseInput from './VaultPassphraseInput';
import { UNLOCK_PASSPHRASE_ERROR, UNLOCK_RETRY_DELAY_MS } from './vaultPassphraseCopy';
import './Vault.css';

/**
 * Cold start's unlock screen — multi-device sync Part B
 * (app/plans/01_08_2026/vault-multidevice/PLAN.md §7).
 *
 * Shown when the probe found an existing vault for this account
 * (`decision === 'found'`) — the device has nothing local yet, so this
 * screen asks for the passphrase that already governs the account's vault,
 * never a NEW one. Distinguishable from the ordinary bootstrap ceremony by
 * its own title/copy: showing "create a passphrase" here is exactly the bug
 * this phase exists to fix.
 *
 * A single secret field, no separate "recovery words" pane — Rust's
 * `open_with_secret` already auto-detects the shape (`looks_like_recovery_phrase`)
 * and tries both slots in the right order, matching what the ordinary
 * `VaultPassphraseUnlockModal` already does. Error copy is the same fixed,
 * no-oracle string that modal already shows for `bad_passphrase`/
 * `vault_kdf_corrupt`/`vault_kdf_unsupported` — no new UX vocabulary here.
 */
export default function VaultColdStartUnlockScreen({ isOpen, onClose, onUnlocked }) {
  const { t } = useTranslation();
  const [secret, setSecret] = useState('');
  const [busy, setBusy] = useState(false);
  const [cooling, setCooling] = useState(false);
  const [error, setError] = useState('');
  const coolTimer = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    setSecret('');
    setBusy(false);
    setCooling(false);
    setError('');
  }, [isOpen]);

  useEffect(
    () => () => {
      if (coolTimer.current) clearTimeout(coolTimer.current);
    },
    []
  );

  const cool = useCallback(() => {
    setCooling(true);
    if (coolTimer.current) clearTimeout(coolTimer.current);
    coolTimer.current = setTimeout(() => setCooling(false), UNLOCK_RETRY_DELAY_MS);
  }, []);

  const failureText = t('vault.unlockFailed', UNLOCK_PASSPHRASE_ERROR);

  const submit = async () => {
    if (busy || cooling || !secret) return;
    setBusy(true);
    setError('');
    try {
      await onUnlocked(secret);
      setSecret('');
    } catch (e) {
      // Deliberately ignores `e` — same no-oracle rule the ordinary unlock
      // modal follows: a wrong secret, a corrupt keyblock and a moved
      // vault_id must all read identically here.
      setError(failureText);
      cool();
    }
    setBusy(false);
  };

  const handleClose = () => {
    if (busy) return;
    setSecret('');
    if (onClose) onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      preventBackdropClose
      size="xl"
      title={t('vault.coldStartTitle', 'Unlock your vault')}
      subtitle={t(
        'vault.coldStartSubtitle',
        'This account already has a vault. Enter your passphrase or recovery phrase to bring it to this device.'
      )}
    >
      <div className="vault-pass-body">
        <VaultPassphraseInput
          id="vault-cold-start-unlock"
          label={t('vault.coldStartField', 'Passphrase or recovery phrase')}
          value={secret}
          onChange={setSecret}
          autoComplete="current-password"
          autoFocus
          disabled={busy}
          onKeyDown={(e) => {
            if (e.key === 'Enter') submit();
          }}
        />
        {error && <div className="vault-modal-error">{error}</div>}

        <div className="modal-footer-row">
          <button
            type="button"
            onClick={handleClose}
            disabled={busy}
            className="px-3 py-1.5 text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors modern-button-secondary text-sm"
          >
            {t('common.cancel')}
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || cooling || !secret}
            className="px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors modern-button text-sm"
          >
            {busy ? t('vault.unlocking', 'Unlocking…') : t('vault.unlock', 'Unlock')}
          </button>
        </div>
      </div>
    </Modal>
  );
}
